import { ApiError } from '../../api/client';
import type { OnboardingFormView } from '../../api/onboardingForm';
import type { PersonAttributes, Resource } from '../../api/types';

/**
 * The silent onboarding tax-id copy (crossmarc/appcore#739).
 *
 * A client a firm onboarded types their (and their spouse's) tax id on the firm's onboarding
 * form. The firm then sets up their household, and the client prefill creates a `person` row
 * for the client and one for the spouse, each stamped `onboardingSubject` (`client`/`spouse`)
 * and `onboardingSource` (`<firmWorkspaceId>#<clientId>`), both `createOnly`. When the client
 * signs in to that household, this copies the number from the form onto the matching row,
 * inside appcore (`POST /people/{id}/tax-id/copy-from-onboarding`): the plaintext never reaches
 * the browser or the façade. Matt's ruling on #739: copy silently. So nothing here is shown.
 *
 * A number the client's ADVISOR entered or replaced (docs/43 §8.4, slice 9) is copied the same
 * way, with no re-entry asked first: appcore copies a staff-set number for the login whose address
 * is the client record's own (appcore docs/06 §35, Matt 2026-11-02). A family member signed in on
 * ANOTHER address gets appcore's ordinary `404 CLIENT_IDENTITY_NOT_FOUND`, which this treats as
 * every other refusal (logged, not marked done, nothing shown). Pinned in `taxIdCopy.test.ts`.
 *
 * ── Which row ─────────────────────────────────────────────────────────────────
 * For each firm on `/me.onboardingInvitations` and each subject, the ONE person in the active
 * workspace whose `onboardingSubject` is that subject and whose `onboardingSource` starts with
 * `<firmWorkspaceId>#`. Zero or several: no call. The SPA cannot know the client id (it is the
 * firm's record id and the SPA never holds it), so it matches the firm half only; appcore
 * matches the whole source against the grant bound to this login, and refuses a row the
 * prefill did not create. So the SPA's choice is a selection, never an authorization.
 *
 * ── "Once" ────────────────────────────────────────────────────────────────────
 * A subject is marked done in the `/me` preferences blob (`onboardingTaxIdCopied`, entries
 * `<workspaceId>#<firmWorkspaceId>#<subject>`) after a copy succeeds, after appcore answers
 * `409 TAX_ID_ALREADY_PRESENT`, or when the row already shows a number. A done subject is never
 * tried again, so a household member clearing the number does not bring it back on the next
 * sign-in. Server-side, per person, for the reason `onboardingSnoozed` and `feedbackSeen` give:
 * localStorage is per browser, and would forget on a new device (and bring a cleared number
 * back) and be shared by everyone on a family computer. Nothing else is terminal: a refusal
 * (a stale step-up, a quota, a form not ready) leaves the subject to be tried at the next
 * trigger. So the steady state is NOT free for every household: a subject with no stamped row
 * (a household with no spouse), one the form does not hold, or one appcore keeps refusing
 * (`COPY_TARGET_NOT_STAMPED`, `CLIENT_IDENTITY_NOT_FOUND`, a role without write) is never done,
 * and each trigger costs a `GET /people` (plus the form read and copy calls when a row matches).
 * Bounded, one run per trigger, never a loop. The one gap: the copy lands and the preferences write fails, and someone clears the
 * number before the next sign-in; then it comes back once.
 *
 * ── Quiet ─────────────────────────────────────────────────────────────────────
 * The people list, the form read and the copy go through the raw client, so
 * `403 STEP_UP_REQUIRED` is an error here and never a ceremony. (The done-write, `PUT /me`, is
 * the caller's and goes through the intercepted client; `/me` is not step-up gated.) Every failure is logged (a code, never a value) and dropped. The first
 * `STEP_UP_REQUIRED` ends the run: every later call in it would meet the same gate.
 */

export type OnboardingSubject = 'client' | 'spouse';
export const ONBOARDING_SUBJECTS: readonly OnboardingSubject[] = ['client', 'spouse'];

/** The preferences key, and how many entries it keeps (newest last) against the 8KB budget. */
export const TAX_ID_COPIED_PREFERENCE = 'onboardingTaxIdCopied';
export const TAX_ID_COPIED_MAX_ENTRIES = 50;

export function copiedKey(workspaceId: string, firmWorkspaceId: string, subject: OnboardingSubject): string {
  return `${workspaceId}#${firmWorkspaceId}#${subject}`;
}

/** The done list with `added` appended, deduplicated and capped to the newest entries. */
export function mergeCopied(existing: unknown, added: readonly string[]): string[] {
  const prior = Array.isArray(existing) ? existing.filter((x): x is string => typeof x === 'string') : [];
  const out = prior.filter(k => !added.includes(k));
  out.push(...added);
  return out.slice(-TAX_ID_COPIED_MAX_ENTRIES);
}

/** The one stamped row for this firm and subject, or `null` when there is not exactly one. */
export function copyTarget(
  people: readonly Resource<PersonAttributes>[], firmWorkspaceId: string, subject: OnboardingSubject,
): Resource<PersonAttributes> | null {
  const prefix = `${firmWorkspaceId}#`;
  const matches = people.filter(p => {
    const a = p.attributes ?? ({} as PersonAttributes);
    return a.onboardingSubject === subject
      && typeof a.onboardingSource === 'string' && a.onboardingSource.startsWith(prefix)
      && a.onboardingSource.length > prefix.length;
  });
  return matches.length === 1 ? matches[0]! : null;
}

export interface TaxIdCopyDeps {
  workspaceId: string;
  firmWorkspaceIds: readonly string[];
  /** The current `onboardingTaxIdCopied` list. */
  done: readonly string[];
  listPeople: () => Promise<Resource<PersonAttributes>[]>;
  getForm: (firmWorkspaceId: string) => Promise<OnboardingFormView>;
  copy: (personId: string, firmWorkspaceId: string, subject: OnboardingSubject) => Promise<void>;
  markDone: (keys: string[]) => Promise<void>;
  log: (event: string, fields: Record<string, string | number>) => void;
}

export interface TaxIdCopyResult {
  copied: number;
  markedDone: string[];
}

function codeOf(err: unknown): string {
  return err instanceof ApiError ? `${err.status}:${err.code}` : 'network';
}

export async function runTaxIdCopy(deps: TaxIdCopyDeps): Promise<TaxIdCopyResult> {
  const result: TaxIdCopyResult = { copied: 0, markedDone: [] };
  const firms = [...new Set(deps.firmWorkspaceIds)];
  const open = (fw: string) => ONBOARDING_SUBJECTS.filter(s => !deps.done.includes(copiedKey(deps.workspaceId, fw, s)));
  if (firms.every(fw => open(fw).length === 0)) return result;

  let people: Resource<PersonAttributes>[];
  try {
    people = await deps.listPeople();
  } catch (err) {
    deps.log('people-unreadable', { code: codeOf(err) });
    return result;
  }

  const newlyDone: string[] = [];
  try {
    for (const fw of firms) {
      const pending: Array<{ subject: OnboardingSubject; personId: string }> = [];
      for (const subject of open(fw)) {
        const target = copyTarget(people, fw, subject);
        if (!target) continue;
        if (typeof target.attributes.taxIdHint === 'string' && target.attributes.taxIdHint !== '') {
          // A number is already there, typed or copied: nothing to do, now or later.
          newlyDone.push(copiedKey(deps.workspaceId, fw, subject));
          continue;
        }
        pending.push({ subject, personId: target.id });
      }
      if (pending.length === 0) continue;

      let form: OnboardingFormView;
      try {
        form = await deps.getForm(fw);
      } catch (err) {
        deps.log('form-unreadable', { code: codeOf(err) });
        if (err instanceof ApiError && err.code === 'STEP_UP_REQUIRED') return result;
        continue;
      }
      const held = new Set((form.identities ?? []).map(i => i.subject));

      for (const { subject, personId } of pending) {
        if (!held.has(subject)) continue;
        try {
          await deps.copy(personId, fw, subject);
          result.copied += 1;
          newlyDone.push(copiedKey(deps.workspaceId, fw, subject));
          deps.log('copied', { subject });
        } catch (err) {
          if (err instanceof ApiError && err.status === 409 && err.code === 'TAX_ID_ALREADY_PRESENT') {
            newlyDone.push(copiedKey(deps.workspaceId, fw, subject));
            deps.log('already-present', { subject });
            continue;
          }
          deps.log('copy-refused', { subject, code: codeOf(err) });
          if (err instanceof ApiError && err.code === 'STEP_UP_REQUIRED') return result;
        }
      }
    }
    return result;
  } finally {
    if (newlyDone.length > 0) {
      try {
        await deps.markDone(newlyDone);
        result.markedDone = newlyDone;
      } catch (err) {
        deps.log('mark-done-failed', { code: codeOf(err) });
      }
    }
  }
}
